import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { CommentIcon } from "@/features/common/components/icons/CommentIcon";
import { scrollToSection } from "@/features/post/helper";
import { PostDetailFragment } from "@/generated/generated-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

import { PostActionShareMenu } from "./PostActionShareMenu";
import { PostLikeButton } from "./PostLikeButton";

interface PostActionBarProps {
  likes: number;
  post: PostDetailFragment;
  isPostOwner: boolean;
  commentCount: number;
}

export const PostActionBar: React.FC<PostActionBarProps> = (props) => {
  const { post, likes, commentCount } = props;
  const { _id, title } = post;
  const router = useRouter();
  const config = getPublicConfig();

  return (
    <div
      className="flex flex-wrap justify-evenly"
      data-test-id="PostActionBar-container"
    >
      <PostLikeButton postId={_id} likes={likes} />

      <Divder />

      <CommentButton commentCount={commentCount} />

      <Divder />

      <PostActionShareMenu postDetail={post} onImageCard={true} />
    </div>
  );
};

const CommentButton = ({ commentCount }: { commentCount: number }) => {
  const { t } = useTranslation("post");

  const onClickComment = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
    scrollToSection({
      anchor: "#comment-section",
      analyticsEnum: AnalyticsEventEnum.PostPageScrollToComment,
    });

  const getCommentLabel = () => {
    const hasComment = commentCount > 0;
    const moreThanOneComments = commentCount > 1;
    if (!hasComment) return t("post:comment.comment");
    if (moreThanOneComments)
      return t("post:comment.commentsHeader", { count: commentCount });
    return t("post:comment.commentHeader", { count: commentCount });
  };

  return (
    <div className="flex items-center touchable" onClick={onClickComment}>
      <CommentIcon alt="comment icon" className="text-gray-300 w-6 h-6" />
      <div className="flex px-2 text-gray-500">{getCommentLabel()}</div>
    </div>
  );
};

const Divder = () => {
  return <span className="w-px my-1 h-auto self-stretch bg-gray-200" />;
};
